import React, { useState } from 'react';
import { ActivityIndicator, Platform, TextInput, View } from 'react-native';
import type { TextInputProps } from 'react-native';
import { cn as dsCn, useTheme } from '@vidahealth/vida-ds';

import { Glyph } from './Glyph';
import ThemedText from './ThemedText';

export interface DsFieldProps extends Omit<TextInputProps, 'style'> {
  label: string;
  error?: string;
  containerStyle?: any;
  loading?: boolean;
  // Rendered inside the field's right edge, e.g. a show/hide toggle.
  accessory?: React.ReactNode;
  // Rendered at the right of the label row, e.g. a "Forgot password?" link.
  labelAccessory?: React.ReactNode;
  errorTestID?: string;
  dataSet?: Record<string, string>;
}

// iOS draws a blurred single-line field that has a line height as wrapped text, clipping the second line.
const SINGLE_LINE_STYLE = Platform.OS === 'ios' ? { lineHeight: undefined } : undefined;

// vida-ds TextField drops `id` (password managers key off it on web) and has no accessory slot.
export const DsField: React.FC<DsFieldProps> = ({
  label,
  error,
  containerStyle,
  loading = false,
  accessory,
  labelAccessory,
  errorTestID,
  onFocus,
  onBlur,
  ...inputProps
}) => {
  const { colors } = useTheme();
  const [focused, setFocused] = useState(false);
  const trailing = loading ? <ActivityIndicator size='small' color={colors['color-text-tertiary']} /> : accessory;

  return (
    <View className='mb-ds-16' style={containerStyle}>
      <View className='mb-ds-8 flex-row items-center justify-between'>
        <ThemedText type='bodySmall'>{label}</ThemedText>
        {labelAccessory}
      </View>
      <View className='relative justify-center'>
        <TextInput
          className={dsCn(
            'h-control-field-notched w-full rounded-ds-md bg-raised px-ds-20 type-b2 text-primary outline-none',
            trailing != null && 'pr-ds-64',
            error
              ? 'border-thick border-status-error'
              : focused
                ? 'border-thick border-focus'
                : 'border-thin border-strong'
          )}
          style={SINGLE_LINE_STYLE}
          placeholderTextColor={colors['color-text-tertiary']}
          autoCapitalize='none'
          onFocus={(e) => {
            setFocused(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            onBlur?.(e);
          }}
          {...inputProps}
        />
        {trailing != null && <View className='absolute right-ds-20'>{trailing}</View>}
      </View>
      {error && (
        <View className='mt-ds-8 flex-row gap-ds-8' testID={errorTestID}>
          <View className='pt-[2px]'>
            <Glyph name='circle-alert' color={colors['color-status-error']} />
          </View>
          <ThemedText type='bodySmall' className='flex-1'>
            {error}
          </ThemedText>
        </View>
      )}
    </View>
  );
};
